import React, { type ChangeEvent, Fragment, useCallback, useEffect, useState } from 'react';

import { styled } from '@compiled/react';
import debounce from 'lodash/debounce';
import { FormattedMessage, useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { cssMap } from '@atlaskit/css';
import { Label } from '@atlaskit/form';
import { CardClient as Client } from '@atlaskit/link-provider';
import { Flex } from '@atlaskit/primitives/compiled';
import TextArea from '@atlaskit/textarea';
import Textfield from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';
import {
	ButtonConfig,
	ButtonStyle,
	isPrivateLoomImageUrl,
	type OnSubmitPayload,
	PanelSubheader,
	useFetchCoverImage,
	useProductDependencies,
} from '@atlassian/company-hub-common';
import { isUnsplashImage } from '@atlassian/company-hub-common/media-components';

import {
	carouselConfigAnalyticSource,
	carouselContentAnalyticSource,
} from '../../../../../../constants';
import { updateLinkAndConfluencePageId } from '../../../../../../controllers/use-carousel-extension-parameters-and-actions';
import { type UpdateFrameFormFieldType } from '../../../../../../controllers/use-carousel-extension-parameters-and-actions/types';
import { type Frame, FRAME_FIELDS, type ResolvedLinkDataObject } from '../../../../../../types';

import { messages } from './messages';

export type FrameFieldProps = {
	frameParameters: Frame;
	isDisabled?: boolean;
	updateFrameFormField: UpdateFrameFormFieldType;
};

const debouncedParametersUpdate = debounce(
	(
		updateFrameFormField,
		inputPropertyToUpdate,
		inputIsUserEditedPropertyToUpdate,
		cardId,
		value,
		createAnalyticsEvent,
		shouldUpdateUserEdited = true,
	) => {
		updateFrameFormField(cardId, {
			[inputPropertyToUpdate]: value,
			[inputIsUserEditedPropertyToUpdate]: shouldUpdateUserEdited,
		});

		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				action: 'edited',
				actionSubject: inputPropertyToUpdate,
				source: carouselConfigAnalyticSource,
			},
		}).fire();
	},
	400,
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const RecommendedWrapper = styled.span({
	textTransform: 'none',
	fontWeight: token('font.weight.regular'),
});

const fieldWrapperStyles = cssMap({
	fieldWrapper: {
		marginTop: token('space.200'),
	},
});

const externalSiteMatcher =
	/^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/;

const getFieldsToUpdateOnLinkSelect = async (
	selectedLink: OnSubmitPayload,
	fetchCoverImageUrl: (
		confluencePageId?: string,
		image?: { url?: string },
	) => Promise<string | undefined>,
	cardImage: string | undefined,
	cardTitle: string | undefined,
	cardDescription: string | undefined,
	isTitleUserEdited: boolean,
	isDescriptionUserEdited: boolean,
	submitError?: (error: Error) => void,
): Promise<Partial<Frame>> => {
	let fieldsToUpdate: Partial<Frame> = updateLinkAndConfluencePageId(selectedLink.url, '', true);

	try {
		const client = new Client();
		const queriedMetadata = (await client.fetchData(selectedLink.url)) as ResolvedLinkDataObject;
		const { name, image, summary } = queriedMetadata?.data || {};
		const { objectId } = queriedMetadata?.meta || {};

		fieldsToUpdate = {
			...fieldsToUpdate,
			...updateLinkAndConfluencePageId(selectedLink.url, objectId, true),
		};

		if (!cardTitle || isTitleUserEdited === false) {
			fieldsToUpdate[FRAME_FIELDS.TITLE] = name;
			fieldsToUpdate[FRAME_FIELDS.IS_TITLE_USER_EDITED] = false;
		}
		if (!cardDescription || isDescriptionUserEdited === false) {
			fieldsToUpdate[FRAME_FIELDS.DESCRIPTION] = summary;
			fieldsToUpdate[FRAME_FIELDS.IS_DESCRIPTION_USER_EDITED] = false;
		}

		// Updating imageSrc and imageAltText
		let newImageSrc: string = cardImage || '';
		const newImageAltText: string = '';
		const coverImage = await fetchCoverImageUrl(objectId, image);
		if (coverImage) {
			const isExternalSiteImage = externalSiteMatcher.test(coverImage);
			if (!isUnsplashImage(coverImage) && !isExternalSiteImage) {
				// For custom uploaded image save it in format `contentId/imageId`
				newImageSrc = `${objectId}/${coverImage}`;
			} else {
				newImageSrc = coverImage;
			}
		}

		// Do not store private Loom CDN thumbnails (they expire via Policy)
		if (isPrivateLoomImageUrl(newImageSrc)) {
			newImageSrc = '';
		}

		fieldsToUpdate = {
			...fieldsToUpdate,
			[FRAME_FIELDS.IMAGE_SRC]: newImageSrc,
			[FRAME_FIELDS.IMAGE_ALT_TEXT]: newImageAltText,
		};
	} catch (error) {
		submitError?.(error as Error);
	}

	return fieldsToUpdate;
};

export const FrameField = ({
	frameParameters,
	updateFrameFormField,
	isDisabled,
}: FrameFieldProps): React.JSX.Element => {
	const {
		cardId,
		link,
		title,
		description,
		isTitleUserEdited,
		isDescriptionUserEdited,
		imageSrc,
		// carousel v2
		buttonStyle,
		buttonText,
	} = frameParameters;

	const { formatMessage } = useIntl();
	const { submitError, cloudId } = useProductDependencies();
	const [titleValue, setTitleValue] = useState(title);
	const [descriptionValue, setDescriptionValue] = useState(description);

	const titleId = `title-field-${cardId}`;
	const descriptionId = `description-field-${cardId}`;

	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		setTitleValue(title);
	}, [title]);

	useEffect(() => {
		setDescriptionValue(description);
	}, [description]);

	const handleDescriptionChange = useCallback(
		(event: ChangeEvent<HTMLTextAreaElement>) => {
			const { value } = event.currentTarget;

			setDescriptionValue(value);
			debouncedParametersUpdate(
				updateFrameFormField,
				FRAME_FIELDS.DESCRIPTION,
				FRAME_FIELDS.IS_DESCRIPTION_USER_EDITED,
				cardId,
				value,
				createAnalyticsEvent,
				carouselConfigAnalyticSource,
			);
		},
		[updateFrameFormField, cardId, createAnalyticsEvent],
	);

	const handleTitleOnChange = useCallback(
		(event: ChangeEvent<HTMLInputElement>) => {
			const { value } = event.currentTarget;
			setTitleValue(value.trimStart());
			debouncedParametersUpdate(
				updateFrameFormField,
				FRAME_FIELDS.TITLE,
				FRAME_FIELDS.IS_TITLE_USER_EDITED,
				cardId,
				value.trimStart(),
				createAnalyticsEvent,
				carouselConfigAnalyticSource,
			);
		},
		[updateFrameFormField, cardId, createAnalyticsEvent],
	);

	const fetchCoverImageUrl = useFetchCoverImage(carouselConfigAnalyticSource);

	const handleSelectLink = useCallback(
		async (selectedLink: OnSubmitPayload) => {
			const fieldsToUpdate = await getFieldsToUpdateOnLinkSelect(
				selectedLink,
				fetchCoverImageUrl,
				imageSrc,
				title,
				description,
				isTitleUserEdited || false,
				isDescriptionUserEdited || false,
				submitError,
			);

			updateFrameFormField(cardId, fieldsToUpdate);
		},
		[
			cardId,
			imageSrc,
			title,
			description,
			isTitleUserEdited,
			isDescriptionUserEdited,
			fetchCoverImageUrl,
			updateFrameFormField,
			submitError,
		],
	);

	const handleClearLink = useCallback(
		(clearAllFields: boolean = false) => {
			const fieldsToClear: Partial<Frame> = {
				...updateLinkAndConfluencePageId('', ''),
				...(clearAllFields ? { buttonText: '', buttonStyle: ButtonStyle.DEFAULT } : {}),
			};

			if (isTitleUserEdited === false) {
				fieldsToClear[FRAME_FIELDS.TITLE] = '';
				fieldsToClear[FRAME_FIELDS.IS_TITLE_USER_EDITED] = undefined;
			}

			if (isDescriptionUserEdited === false) {
				fieldsToClear[FRAME_FIELDS.DESCRIPTION] = '';
				fieldsToClear[FRAME_FIELDS.IS_DESCRIPTION_USER_EDITED] = undefined;
			}

			updateFrameFormField(cardId, fieldsToClear);
		},
		[updateFrameFormField, cardId, isTitleUserEdited, isDescriptionUserEdited],
	);

	const handleClearButton = () => {
		handleClearLink(true);
	};

	const updateButtonText = useCallback(
		(buttonTextPayload?: string) => {
			updateFrameFormField(cardId, { buttonText: buttonTextPayload });
		},
		[updateFrameFormField, cardId],
	);

	const updateButtonStyle = useCallback(
		(buttonStylePayload: ButtonStyle) => {
			updateFrameFormField(cardId, { buttonStyle: buttonStylePayload });
		},
		[updateFrameFormField, cardId],
	);

	return (
		<Fragment>
			<ButtonConfig
				cloudId={cloudId}
				link={link}
				text={buttonText}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-1876
				style={buttonStyle}
				handleSelectLink={handleSelectLink}
				handleClearLink={handleClearLink}
				handleClearButton={handleClearButton}
				updateButtonText={updateButtonText}
				updateButtonStyle={updateButtonStyle}
				source={carouselContentAnalyticSource}
			/>
			<Flex xcss={fieldWrapperStyles.fieldWrapper} direction="column">
				<Label htmlFor={titleId}>
					<PanelSubheader showUpdatedStyles>
						<FormattedMessage {...messages.titleFieldLabel} />
						<RecommendedWrapper>
							{' '}
							<FormattedMessage {...messages.recommendedFieldLabel} />
						</RecommendedWrapper>
					</PanelSubheader>
				</Label>
				<Textfield
					id={titleId}
					value={titleValue?.trimStart()}
					onChange={handleTitleOnChange}
					maxLength={255}
					isDisabled={isDisabled}
					placeholder={formatMessage(messages.titlePlaceholder)}
				/>
			</Flex>
			<Flex xcss={fieldWrapperStyles.fieldWrapper} direction="column">
				<Label htmlFor={descriptionId}>
					<PanelSubheader showUpdatedStyles>
						<FormattedMessage {...messages.descriptionFieldLabel} />
					</PanelSubheader>
				</Label>
				<TextArea
					id={descriptionId}
					rows={3}
					value={descriptionValue?.trimStart()}
					onChange={handleDescriptionChange}
					maxLength={700}
					isDisabled={isDisabled}
					testId="frame-description-field"
					placeholder={formatMessage(messages.descriptionPlaceholder)}
				/>
			</Flex>
		</Fragment>
	);
};
